<template lang="pug">
Drawer.add-gym-drawer(@hide="$emit('close')" :title="editGym ? 'Edit Your Gym' : 'Add Your Gym'")
  DrawerContents
    loading(v-if="isLoading")
    .gym-already-added(v-else-if="alreadyHasGym && !editGym")
      Message(linkText="My Gym" linkUrl="/account/my-gyms") {{ __('Your gym has already been created. You can edit your gym by navigating to the gyms page in My Account.') }}
    .add-gym-form(ref="gymForm" v-else-if="!isSubmitted")
      ProgressBar.progress-bar(:current-step="currentStep" :total-steps="totalSteps" :no-margin="true")
      AddGymInformation(v-if="currentStep === 1" v-model="gymData" ref="gymInformationForm" :agreeToMapNotice="isPublic" @agree="isPublic = $event" :step="currentStep")
      AddGymTrainingType(v-if="currentStep === 2 || currentStep === 3" v-model="gymData" ref="gymTrainingTypeForm" :isPrimary="currentStep === 2" :step="currentStep" :isEdit="!!editGym")
      AddGymMedia(
        v-if="currentStep === mediaStep"
        v-model="gymData"
        :logoSrc="logoSrc"
        :step="currentStep"
        @uploadLogo="uploadLogo($event)"
        @clearLogo="clearLogo"
        :uploadMeta="uploadMediaMeta"
        @uploadMedia="uploadMedia($event)"
        @removeUpload="removeMedia($event)"
        ref="gymMediaForm"
      )
      AddGymEquipment(v-if="currentStep === 5" v-model="gymData.products" :isEdit="!!editGym" :step="currentStep")
      .error(v-if="submissionError") {{ __(submissionError) }}
    GymSubmitted(v-else-if="isSubmitted")
  template(#footer)
    .footer-buttons
      RogueButton.btn(v-if="!isSubmitted" color="gray" width="full" @click='prevStep' :text="currentStep === 1 ? 'Cancel' : 'Back'")
      RogueButton.btn(v-if="!isSubmitted && (!alreadyHasGym || editGym)" color="black" width="full" @click='nextStep' :text="nextText" :loading="isSubmitting" :disabled="nextDisabled")
      RogueButton.btn(v-if="isSubmitted" color="gray" width="full" @click="$emit('close')" text="Close")
</template>
<script setup lang="ts">
import { ref, computed, nextTick, type PropType, onMounted } from 'vue'
import type { IGym } from '@rogue/universal'
import DOMPurify from 'dompurify'
import { getCloudinaryUrl } from 'sisu/js/helpers'
import AddGymInformation from './AddGymInformation.vue'
import AddGymTrainingType from './AddGymTrainingType.vue'
import AddGymMedia from './AddGymMedia.vue'
import AddGymEquipment from './AddGymEquipment.vue'
import GymSubmitted from './GymSubmitted.vue'
import ProgressBar from '@/js/components/MultiStepProgressBar.vue'
import { useRogue } from '@/js/services/Rogue'
import { useCustomerStore } from '@/js/stores/customerStore'

const props = defineProps({
  editGym: {
    type     : Object as PropType<IGym & {externalId: string}>,
    required : false,
  },
})

const emit = defineEmits<{(e: 'close'): void, (e: 'added'): void}>()

const isLoading = ref(true)
const isSubmitted = ref<boolean>(false)
const hasMediaChanges = ref<boolean>(false)

const rogue = useRogue()
const customerStore = useCustomerStore()

const gymForm = ref<HTMLElement>()
const gymInformationForm = ref<typeof AddGymInformation>()
const gymTrainingTypeForm = ref<typeof AddGymTrainingType>()
const gymMediaForm = ref<typeof AddGymMedia>()

const mediaStep = 4
const totalSteps = 5

const currentStep = ref(1)
const gymData = ref<Partial<IGym>>(props.editGym ? JSON.parse(JSON.stringify(props.editGym)) : {
  name          : '',
  type          : '',
  customType    : '',
  trainingTypes : [],
  allowDropIns  : false,
  shareAddress  : true,
  email         : customerStore.email || '',
  social        : [{
    url  : '',
    type : 'website',
  }],
  description : '',
  address     : {
    city      : '',
    street    : [],
    region    : '',
    postcode  : '',
    countryId : '',
  },
  products: [],
})
const logo = ref<File | null>(null)
const media = ref<File[]>([])
const logoSrc = ref(gymData.value.logo?.src ? getCloudinaryUrl(gymData.value.logo?.src) : '')
const uploadMediaMeta = ref<{ src: string, isVideo: boolean, duration?: string }[]>([])
const isSubmitting = ref(false)
const submissionError = ref<string | null>(null)
const isPublic = ref(!!props.editGym)
const alreadyHasGym = ref(false)

const isFormValid = computed<boolean>(() => {
  if (currentStep.value === mediaStep) {
    return !gymMediaForm.value || gymMediaForm.value.isValid
  }

  return currentStep.value > 3 || (gymInformationForm.value && gymInformationForm.value.isValid) || (gymTrainingTypeForm.value && gymTrainingTypeForm.value.isValid)
})

const nextDisabled = computed<boolean>(() => !isFormValid.value || isSubmitting.value)
const nextText = computed<string>(() => {
  if (currentStep.value === totalSteps) {
    return 'Submit'
  }

  if (currentStep.value === mediaStep) {
    return (hasMediaChanges.value || gymData.value.description || gymData.value.social?.find(s => s.url)) ? 'Next' : 'Skip'
  }

  return 'Next'
})

function uploadLogo(file: File) {
  hasMediaChanges.value = true
  logo.value = file
  gymData.value.logo = undefined
  logoSrc.value = URL.createObjectURL(file)
}

function clearLogo() {
  hasMediaChanges.value = true
  logo.value = null
  gymData.value.logo = undefined
  logoSrc.value = ''
}

function uploadMedia(file: File) {
  hasMediaChanges.value = true
  media.value.push(file)
  const src = URL.createObjectURL(file)
  const isVideo = file.type.includes('video')
  const newIndex = uploadMediaMeta.value.length
  const duration = isVideo ? '' : undefined
  if (isVideo) {
    const video = document.createElement('video')
    video.preload = 'metadata'

    video.onloadedmetadata = () => {
      const seconds = video.duration
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const remainingSeconds = Math.round(seconds % 60)
      const formattedTime = `${hours > 0 ? `${hours.toString()}:` : ''}${minutes.toString().padStart(1, '0')}:${remainingSeconds.toString().padStart(2, '0')}`

      uploadMediaMeta.value[newIndex].duration = formattedTime
    }

    video.src = src
  }
  uploadMediaMeta.value.push({ src, isVideo, duration })
}

function removeMedia(index: number) {
  hasMediaChanges.value = true
  media.value.splice(index, 1)
  uploadMediaMeta.value.splice(index, 1)
}

function sanitizeInput(input: string) {
  return DOMPurify.sanitize(input)
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .trim()
}

async function submitForm() {
  submissionError.value = null
  isSubmitting.value = true
  nextTick()

  if (gymData.value.type === 'Home Gym') {
    gymData.value.shareAddress = false
  }

  // Clear out any empty social links and make sure all sites use https
  if (gymData.value.social) {
    gymData.value.social = gymData.value.social.filter(s => s.url)
    gymData.value.social.forEach(link => {
      link.url = link.url.replace('http://', 'https://')
      if (!link.url.startsWith('https://')) {
        link.url = `https://${link.url}`
      }
    })
  }

  // Sanitize user input for name and description
  if (gymData.value.name) {
    gymData.value.name = sanitizeInput(gymData.value.name)
  }

  if (gymData.value.description) {
    gymData.value.description = sanitizeInput(gymData.value.description)
  }

  try {
    const formData = new FormData()
    media.value.forEach(file => formData.append('media', file, file.name))
    if (logo.value) {
      formData.append('logo', logo.value, logo.value.name)
    }
    formData.append('data', JSON.stringify(gymData.value))

    if (props.editGym) {
      await rogue.fetchWithCaptcha(`/svc_gym/site/${rogue.site.code}/customer/gyms`, 'POST', formData, { 'Content-Type': 'multipart/form-data' }, 'json', 'gym_add')
      rogue.track.track({ type: 'custom', event: 'gymEdit', data: { gymName: gymData.value.name, gymId: props.editGym.externalId } })
    } else {
      await rogue.fetchWithCaptcha(`/svc_gym/site/${rogue.site.code}/customer/gyms`, 'POST', formData, { 'Content-Type': 'multipart/form-data' }, 'json', 'gym_add')
      rogue.track.track({ type: 'custom', event: 'gymAdded', data: { gymName: gymData.value.name } })
    }

    isSubmitted.value = true
  } catch (e) {
    rogue.log.error(props.editGym ? 'Error submitting gym changes' : 'Error adding new gym', e)
    submissionError.value = props.editGym ? 'An error occurred while saving your gym changes. Please try again.' : 'An error occurred while submitting your gym. Please try again.'
  }
  isSubmitting.value = false
  emit('added')
}

function nextStep() {
  if (currentStep.value === mediaStep && gymMediaForm.value && !gymMediaForm.value.validate()) {
    return
  }

  if (currentStep.value === totalSteps) {
    submitForm()
  } else {
    rogue.track.track({ type: 'custom', event: 'addGymStepCompleted', data: { step: currentStep.value } })
    currentStep.value += 1
  }
}

function prevStep() {
  if (currentStep.value === 1) {
    emit('close')
  }

  submissionError.value = null
  currentStep.value -= 1
}

onMounted(async () => {
  if (!props.editGym) {
    try {
      const gyms = await rogue.fetch<IGym[]>(`svc_gym/site/${rogue.site.code}/customer/gyms`)
      if (gyms?.length) {
        alreadyHasGym.value = true
      }
    } catch (e) {
      rogue.log.error('Unable to load gyms for this customer', e as Error)
    }
  }
  isLoading.value = false
})

</script>
<style lang="scss" scoped>
.progress-bar {
  margin-top: 0;
  margin-bottom: $space-s;
}

.error {
  color: $color-red;
  font-size: $size-xs;
  margin-top: $space-xxxs;
}

.footer-buttons {
  box-shadow: 0px -2px 8px 0px $color-gray-light;
  display: flex;
  gap: $space-s;
  padding: $space-s;
}
</style>

<style lang="scss" scoped target="app">
.platform-ios,
.platform-android {
  .footer-buttons .btn {
    margin-bottom: 0 !important;
  }
}
</style>
